import { INGXLoggerMonitor } from "ngx-logger";
import {
  getLogLevelDisplayName,
  writeToConsole,
} from "app/shared/helpers/logger-monitor-utils";
import { INGXLoggerMetadata } from "ngx-logger";

// Used to improve the readability of console logs in the browser.
// This monitor hooks into NGXLogger and renders all logs to the console.
// To improve readability we equalize indentation across lines and use colors to highlight logs of the same file.
export class LoggerMonitorDevelopment implements INGXLoggerMonitor {
  // Capture group 1 catches the file name without extension.
  // We ignore the path and extension to remove clutter from the output.
  private fileNameRegExp: RegExp = /([^\/]+.)\..*$/;

  private borderSize: string = "0.32rem";
  private fileNameBorderRadiusTopLeft: string = "1.28rem";

  public onLog(log: INGXLoggerMetadata): void {
    // Preparing data:

    const logLevelDisplayName: string = getLogLevelDisplayName(log.level);
    // Using the full path to generate the HUE.
    // Making sure files with the same name, but different directories, have different colors.
    if (!log.fileName) return;

    const fileNameHue: number = LoggerMonitorDevelopment.getHslHueOfString(
      log.fileName,
    );
    const fileNameMatch: RegExpMatchArray | null = log.fileName.match(
      this.fileNameRegExp,
    );
    const fileName: string = fileNameMatch ? fileNameMatch[1] : "";

    // CSS snippets:
    const fileBackgroundColor: string = `hsl(${fileNameHue}, 100%, 70%)`;

    const fileBorder: string = `${this.borderSize} solid ${fileBackgroundColor}`;

    // While styling keep in mind there is light and dark theme for the dev-tools.
    const args: string[] = [
      `%c${logLevelDisplayName} %c ${fileName}%c:${log.lineNumber} %c ${log.message}`,
      // Log-level:
      `${LoggerMonitorDevelopment.getCssBorderLeftForFirstElementInLine(
        log.level,
      )}`,
      // File name:
      `
      background: ${fileBackgroundColor};
      color: #222;
      border-top: ${fileBorder};
      border-radius: ${this.fileNameBorderRadiusTopLeft} 0 0 0;
      `,
      // File line number:
      `
      background: ${fileBackgroundColor};
      color: #555;
      border-top: ${fileBorder};
      `,
      // Message:
      ``, // No special style.
    ];

    writeToConsole(log, ...args);
  }

  // Compensating for the Chrome expand arrow displayed for TRACE, WARN and ERROR logs.
  // We add a free space where the expand arrow would be for all other log-levels.
  // This ensures a equal indent of the file names.
  private static getCssBorderLeftForFirstElementInLine(
    logLevel: number,
  ): string {
    return logLevel === 0 || logLevel > 3
      ? ""
      : `border-left: .65rem solid transparent;`;
  }

  private static getHslHueOfString(value: string): number {
    return LoggerMonitorDevelopment.getHashCode(value) % 360;
  }

  // Inspired by:
  // https://stackoverflow.com/a/21682946
  private static getHashCode(input: string): number {
    let hash: number = 0;
    if (input.length === 0) {
      return hash;
    }

    for (let i: number = 0; i < input.length; i++) {
      hash = input.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  }
}
